import React from 'react';
import { Link } from 'react-router-dom';
import { certificationsData } from '../data/mock';
import { CheckCircle2 } from 'lucide-react';

const Compliance = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container text-center">
          <h1 className="hero-large mb-6">Certifications & Compliance</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            Full compliance with national and international healthcare standards ensuring safety, quality, and regulatory adherence across all projects
          </p>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {certificationsData.map((category, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '40px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <h3 className="heading-2 mb-6">{category.category}</h3>
                <ul className="space-y-3">
                  {category.certifications.map((cert, certIndex) => (
                    <li key={certIndex} className="flex items-start gap-3">
                      <CheckCircle2 
                        size={20} 
                        style={{ 
                          color: 'var(--interactive-base)', 
                          flexShrink: 0,
                          marginTop: '2px'
                        }} 
                      />
                      <span className="body-large" style={{ color: 'var(--text-primary)' }}>
                        {cert}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Compliance Information */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium mb-12 text-center">Comprehensive Compliance Framework</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Medical & Clinical Standards */}
            <div>
              <h3 className="heading-2 mb-6">Medical & Clinical Standards</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    NMC (National Medical Commission)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Complete adherence to NMC guidelines for medical college infrastructure, teaching hospitals, and clinical training facilities.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    NABH (National Accreditation Board for Hospitals)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Design and construction adhering to NABH accreditation standards for quality healthcare delivery and patient safety.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    ICMR (Indian Council of Medical Research)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Infrastructure meeting ICMR standards for research institutes, virology laboratories, and medical research facilities.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    IPHS (Indian Public Health Standards)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Compliance with IPHS guidelines for primary, secondary, and tertiary healthcare facilities across India.
                  </p>
                </div>
              </div>
            </div>

            {/* Engineering & Safety Standards */}
            <div>
              <h3 className="heading-2 mb-6">Engineering & Safety Standards</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    HTM (Health Technical Memorandum)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    UK-based healthcare engineering standards for ventilation, medical gas systems, and specialized healthcare environments.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    NFPA (National Fire Protection Association)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Complete fire safety compliance including detection, suppression systems, and emergency evacuation protocols.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    NBC (National Building Code)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Structural design, construction practices, and building services compliance with national building standards.
                  </p>
                </div>

                <div>
                  <h4 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                    ISO (International Organization for Standardization)
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Quality management systems, environmental management, and occupational health and safety standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Compliance */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium mb-12 text-center">Specialized Healthcare Compliance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}
            >
              <h3 className="heading-2 mb-4">National Organ Transplant Society</h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Specialized infrastructure compliance for organ transplant facilities including operation theatres, ICUs, storage, and patient care areas meeting transplant society requirements.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}
            >
              <h3 className="heading-2 mb-4">PMABIM (Pradhan Mantri Ayushman Bharat Infrastructure Mission)</h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Compliance with PMABIM guidelines for strengthening healthcare infrastructure across primary, secondary, and tertiary care levels.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}
            >
              <h3 className="heading-2 mb-4">NGHCT (National Guidelines for Hospital Cleanliness)</h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Infrastructure designed to meet national cleanliness standards ensuring infection control, waste management, and hygienic healthcare delivery.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}
            >
              <h3 className="heading-2 mb-4">PESO (Petroleum and Explosives Safety Organisation)</h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Safety compliance for medical gas systems, oxygen storage, and compressed gas handling in healthcare facilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Laboratory Standards */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">Laboratory & Biosafety Standards</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Specialized infrastructure capability for advanced medical research and diagnostic laboratories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4">NBS Laboratory</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                National Biosafety Standard laboratory infrastructure for advanced medical research
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4">BSL-2 Facilities</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Biosafety Level 2 laboratory design and construction for moderate-risk pathogen research
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '40px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4">BSL-3 Facilities</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Biosafety Level 3 containment facilities for high-risk infectious agents and virology research
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding cta-section-dark">
        <div className="container text-center">
          <h2 className="hero-medium mb-4 cta-heading-light">
            Compliance-Driven Healthcare Infrastructure
          </h2>
          <p className="body-large mb-8 cta-text-light">
            Every project delivered with complete regulatory adherence and certification support.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary btn-light-on-dark"
          >
            Request Project Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
